'use client';

import { Suspense, useEffect } from 'react';
import { usePathname, useSearchParams } from 'next/navigation';
import { datadogRum } from '@datadog/browser-rum';
import { initializeDatadog } from '@/lib/datadog';
import { applyRumProductFromPathname } from '@/lib/analytics/rumProduct';
import { subscribePublicSearchGridStoreAfterHydration } from '@/lib/analytics/rumGridStoreSubscriber';

// `useSearchParams()` requires a Suspense boundary in Next.js 13+. Isolating
// it in a separate component lets `children` render regardless of the
// Suspense state while keeping the page-view tracking self-contained.
function DatadogPageTracker() {
  const pathname = usePathname();
  const searchParams = useSearchParams();

  useEffect(() => {
    if (typeof window === 'undefined') return;

    const run = () => {
      initializeDatadog();
      applyRumProductFromPathname(pathname);

      const url = `${pathname}?${searchParams}`;
      if (datadogRum.getInitConfiguration()) {
        datadogRum.addAction('page_view', { url });
      }
    };

    const win = window as Window & {
      requestIdleCallback?: typeof window.requestIdleCallback;
      cancelIdleCallback?: typeof window.cancelIdleCallback;
    };
    if (typeof win.requestIdleCallback === 'function') {
      const handle = win.requestIdleCallback(run, { timeout: 2000 });
      return () => win.cancelIdleCallback?.(handle);
    }
    const handle = win.setTimeout(run, 1);
    return () => win.clearTimeout(handle);
  }, [pathname, searchParams]);

  return null;
}

export function DatadogProvider({ children }: { children: React.ReactNode }) {
  useEffect(() => {
    if (typeof window === 'undefined') return;
    return subscribePublicSearchGridStoreAfterHydration();
  }, []);

  return (
    <>
      <Suspense fallback={null}>
        <DatadogPageTracker />
      </Suspense>
      {children}
    </>
  );
}
